import { useState } from "react"
import { router } from "@inertiajs/react"
import type { Page } from "@inertiajs/core"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Card } from "@/components/ui/Card"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/Tabs"
import { currencyDisplay, formatMoneyFull } from "@/lib/money"
import { push_review_action_path, push_review_actions_path, push_review_path } from "@/lib/routes"
import { useMarket } from "@/lib/useMarket"
import type { ActionDraft } from "./review/components/ActionEditor"
import ListSettings from "./review/components/ListSettings"
import ProductPanel from "./review/components/ProductPanel"
import ReviewList from "./review/components/ReviewList"
import { useListSettings } from "./review/hooks/useListSettings"
import type { ReviewProps } from "./review/types"

// Switching product reloads only what the panel shows; the list stays.
const PANEL_PROPS = ["selected", "detail", "actions", "mentioned_by", "mention_names"]

// The Push review on one shared screen, one product at a time. Tabs are the
// product groups (and Other); the left lists the group's products whose days
// of cover reach the cut-off and whose excess value reaches the minimum,
// biggest excess value first; the right is the product being discussed and the
// actions decided for it.
export default function PushReview(props: ReviewProps) {
  const market = useMarket()
  const groupOf = (externalId: string | null) => props.rows.find((r) => r.external_id === externalId)?.group_id
  const firstWithRows = props.groups.find((g) => props.rows.some((r) => r.group_id === g.id))?.id
  const [group, setGroup] = useState(groupOf(props.selected) ?? firstWithRows ?? props.groups[0]?.id ?? null)
  const listSettings = useListSettings({
    marketSlug: market.slug,
    path: push_review_path(market.slug),
    current: { doc: props.cutoff.value, min: props.min_excess_value.value },
    defaults: { doc: props.cutoff.default, min: props.min_excess_value.default },
  })

  const rows = props.rows.filter((r) => r.group_id === group)
  const countIn = (groupId: string) => props.rows.filter((r) => r.group_id === groupId).length

  const show = (externalId: string, groupId: string | null) =>
    router.get(
      push_review_path(market.slug),
      { group: groupId ?? undefined, product: externalId, ...listSettings.params },
      { preserveState: true, preserveScroll: true, replace: true, only: PANEL_PROPS },
    )

  const pickGroup = (groupId: string) => {
    setGroup(groupId)
    const first = props.rows.find((r) => r.group_id === groupId)
    if (first) show(first.external_id, groupId)
  }

  // Each change goes to the server and comes back to the same product.
  const visitOptions = (done?: (saved: boolean) => void) => ({
    preserveState: true,
    preserveScroll: true,
    // A refusal comes back as a redirect with an alert.
    onSuccess: (page: Page) => done?.(!(page.props.flash as Record<string, string> | undefined)?.alert),
    onError: () => done?.(false),
  })
  const create = (draft: ActionDraft, done: (saved: boolean) => void) =>
    router.post(push_review_actions_path(market.slug), { ...draft, product_external_id: props.selected, group, ...listSettings.params }, visitOptions(done))
  const update = (id: string, draft: ActionDraft, done: (saved: boolean) => void) =>
    router.patch(push_review_action_path(market.slug, id), { ...draft, group, ...listSettings.params }, visitOptions(done))
  const remove = (id: string) =>
    router.delete(push_review_action_path(market.slug, id), { data: { group, ...listSettings.params }, ...visitOptions() })

  const panelShown = props.detail && rows.some((r) => r.external_id === props.selected)

  return (
    <AppLayout header={<PageTitle title="Push Review" subtitle="Go through the products with too much stock, one group at a time, and decide how to push each one." />}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={group ?? undefined} onValueChange={pickGroup}>
          <TabsList variant="nav">
            {props.groups.map((g) => (
              <TabsTrigger key={g.id} variant="nav" value={g.id} className="text-sm">
                {g.name} <span className="ml-1 tabular-nums text-muted-foreground">{countIn(g.id)}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <ListSettings
          key={`${props.cutoff.value}-${props.min_excess_value.value}`}
          value={{ doc: props.cutoff.value, min: props.min_excess_value.value }}
          defaults={{ doc: props.cutoff.default, min: props.min_excess_value.default }}
          docRange={{ min: props.cutoff.min, max: props.cutoff.max }}
          currency={market.currency}
          onApply={(settings) => listSettings.apply(settings, { group: group ?? undefined })}
        />
      </div>

      {rows.length === 0 ? (
        <Card className="px-6 py-10 text-center text-sm">
          No products in this group have {props.cutoff.value} or more days of stock and an excess value of{" "}
          {formatMoneyFull(props.min_excess_value.value, market.currency)} {currencyDisplay(market.currency).symbol} or more.
        </Card>
      ) : (
        <div className="grid grid-cols-[600px_minmax(0,1fr)] items-start gap-5">
          <ReviewList
            rows={rows}
            selectedId={props.selected}
            actionCounts={props.action_counts}
            showValue={props.can_see_value}
            cutoff={props.cutoff.value}
            currency={market.currency}
            onSelect={(id) => show(id, group)}
          />
          {panelShown && props.detail ? (
            <ProductPanel
              key={props.detail.external_id}
              detail={props.detail}
              actions={props.actions}
              mentionedBy={props.mentioned_by}
              names={props.mention_names}
              actionTypes={props.action_types}
              channels={props.channels}
              people={props.people}
              onCreate={create}
              onUpdate={update}
              onDelete={remove}
            />
          ) : (
            <Card className="px-6 py-10 text-center text-sm">Pick a product on the left.</Card>
          )}
        </div>
      )}
    </AppLayout>
  )
}
